import { ChangeEvent, useMemo, useState } from 'react';
import { useCountriesQuery } from '@access-cms/api-client';
import { NON_ALPHANUMERIC_CHARS } from '@access-cms/shared-constants';

import { DatePicker } from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { passportNumber, required } from '../../utils/fieldValidation';

export interface PassportInformationFormData {
  passportIssuanceCountry: string;
  passportIssuanceCountryCode: string;
  passportType: string;
  passportNumber: string;
  passportIssueDate: string;
  passportExpirationDate: string;
}

const passportTypeOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Regular/Ordinary', value: 'Regular/Ordinary' },
  { label: 'Official', value: 'Official' },
  { label: 'Diplomatic', value: 'Diplomatic' },
  { label: 'Emergency', value: 'Emergency' },
  { label: 'Temporary', value: 'Temporary' },
];

function PassportInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<PassportInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'passport-information-page';
  const countriesQuery = useCountriesQuery({ excludeUs: false });

  const countries = useMemo(() => countriesQuery.data?.data ?? [], [countriesQuery.data?.data]);
  const countryOptions = useMemo(() => {
    const options = countries
      .filter((country) => country.countryCode && country.countryName)
      .map((country) => ({
        label: country.countryName as string,
        value: country.countryCode as string,
      }));
    const usOption = options.find((option) => option.value === 'US');

    return [
      { label: '- Start typing to filter results -', value: '' },
      ...(usOption ? [usOption] : []),
      ...options.filter((option) => option.value !== 'US'),
    ];
  }, [countries]);
  const selectedPassportIssuanceCountryCode = useMemo(() => {
    if (data.passportIssuanceCountryCode) return data.passportIssuanceCountryCode;

    return countries.find((country) => country.countryName === data.passportIssuanceCountry)?.countryCode ?? '';
  }, [countries, data.passportIssuanceCountry, data.passportIssuanceCountryCode]);

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'passportIssuanceCountry', label: 'Passport Issuance Country', validationRules: [required] },
      { field: 'passportType', label: 'Passport Type', validationRules: [required] },
      { field: 'passportNumber', label: 'Passport Number', validationRules: [required, passportNumber] },
      { field: 'passportIssueDate', label: 'Passport Issue Date', validationRules: [required] },
      { field: 'passportExpirationDate', label: 'Passport Expiration Date', validationRules: [required] },
    ],
  });

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 50);
  const maxFutureDate = new Date();
  maxFutureDate.setFullYear(today.getFullYear() + 20);

  const handleChange = (field: keyof PassportInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleIssuanceCountryChange = (countryCode: string) => {
    const selectedCountry = countries.find((country) => country.countryCode === countryCode);
    const passportIssuanceCountry = selectedCountry?.countryName ?? '';

    updateData({
      ...data,
      passportIssuanceCountry,
      passportIssuanceCountryCode: countryCode,
    });
    handleValidationChange('passportIssuanceCountry', passportIssuanceCountry);
  };

  // Format Passport Number: alphanumeric, typically 6-9 characters
  const handlePassportNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toUpperCase(); // Passport numbers are typically uppercase
    // Allow alphanumeric characters only
    const cleaned = input.replace(NON_ALPHANUMERIC_CHARS, '');
    // Limit to 15 characters (generous limit to accommodate various international formats)
    const limited = cleaned.slice(0, 15);

    updateData({
      ...data,
      passportNumber: limited,
    });
    handleValidationChange('passportNumber', limited);
  };

  const passportIssuanceCountryError =
    getFieldError('passportIssuanceCountry') ||
    (countriesQuery.isError ? 'Unable to load countries. Please refresh and try again.' : undefined);

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-issuance-country`}>
        <Dropdown
          required
          searchable
          data-testid={`${baseTestId}-issuance-country-dropdown`}
          disabled={countriesQuery.isLoading || countriesQuery.isError}
          errorMessage={passportIssuanceCountryError}
          hint='Start typing to filter country results'
          label='Passport Issuance Country'
          name='passportIssuanceCountry'
          options={countryOptions}
          tooltipText='The country whose government issued your passport. This is the country that issued your travel document, shown on the information page of your passport.'
          value={selectedPassportIssuanceCountryCode || ''}
          onBlur={() => handleBlur('passportIssuanceCountry')}
          onChange={handleIssuanceCountryChange}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-passport-type`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-passport-type-dropdown`}
          errorMessage={getFieldError('passportType')}
          hint='Select the type of passport you hold'
          label='Passport Type'
          name='passportType'
          options={passportTypeOptions}
          tooltipText='The type of passport you hold. Most individuals have a Regular/Ordinary passport. Official and Diplomatic passports are issued to government officials. Emergency and Temporary passports are issued for specific situations.'
          value={data.passportType || ''}
          onBlur={() => handleBlur('passportType')}
          onChange={(value: string) => handleChange('passportType', value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-passport-number`}>
        <TextField
          required
          data-testid={`${baseTestId}-passport-number-field`}
          errorMessage={getFieldError('passportNumber')}
          hint='6-15 alphanumeric characters. Found on the information page of your passport.'
          label='Passport Number'
          name='passportNumber'
          tooltipText='The unique alphanumeric identifier for your passport (6-15 characters). Located on the information page (usually page 2) of your passport. Accepts letters and numbers only, no special characters. Format varies by country (e.g., US: 9 digits, UK: 9 digits, Germany: letter + 8-10 digits).'
          value={data.passportNumber || ''}
          onBlur={() => handleBlur('passportNumber')}
          onChange={handlePassportNumberChange}
        />
      </div>

      <div
        className='ds-u-display--flex ds-u-flex-wrap--wrap ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-dates`}>
        <div
          className='ds-u-margin-right--3 ds-u-margin-bottom--2'
          data-testid={`${baseTestId}-issue-date`}>
          <DatePicker
            required
            data-testid={`${baseTestId}-issue-date-picker`}
            errorMessage={getFieldError('passportIssueDate')}
            fromDate={minDate}
            id='passportIssueDate'
            label='Passport Issue Date'
            name='passportIssueDate'
            toDate={today}
            tooltipText='The date your passport was issued by the issuing country, as shown on the information page of your passport.'
            value={data.passportIssueDate || ''}
            onBlur={() => handleBlur('passportIssueDate')}
            onDateChange={(date, formattedDate) => handleChange('passportIssueDate', formattedDate || '')}
          />
        </div>

        <div
          className='ds-u-margin-bottom--2'
          data-testid={`${baseTestId}-expiration-date`}>
          <DatePicker
            required
            data-testid={`${baseTestId}-expiration-date-picker`}
            errorMessage={getFieldError('passportExpirationDate')}
            fromDate={today}
            id='passportExpirationDate'
            label='Passport Expiration Date'
            name='passportExpirationDate'
            toDate={maxFutureDate}
            tooltipText='The date your passport expires, as shown on the information page of your passport. Many countries require that your passport be valid for at least 6 months beyond your intended travel dates.'
            value={data.passportExpirationDate || ''}
            onBlur={() => handleBlur('passportExpirationDate')}
            onDateChange={(date, formattedDate) => handleChange('passportExpirationDate', formattedDate || '')}
          />
        </div>
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Passport Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Passport Details</h3>
          <p>
            Please enter your passport details. We need this information to verify who you are and to see if you can
            travel between countries.
          </p>

          <h4 className='ds-u-margin-top--3'>Passport Issuance Country</h4>
          <p>
            Select the country that issued your passport. This is the country whose government issued your travel
            document. Start typing to filter the list of countries.
          </p>

          <h4 className='ds-u-margin-top--3'>Passport Type</h4>
          <p>Select the type of passport you hold:</p>
          <ul>
            <li>
              <strong>Regular/Ordinary:</strong> The most common type of passport that is issued to citizens to travel
              between countries.
            </li>
            <li>
              <strong>Official:</strong> For government officials who need to travel on official business.
            </li>
            <li>
              <strong>Diplomatic:</strong> For diplomats and high-ranking government officials.
            </li>
            <li>
              <strong>Emergency:</strong> A short-term passport issued quickly in emergencies.
            </li>
            <li>
              <strong>Temporary:</strong> A passport that is valid only for a short time and for a specific reason.
            </li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Passport Number</h4>
          <p>
            Please enter your passport number as it appears on your passport. This is typically found on the information
            page of your passport and may contain letters and numbers (6-15 characters). The format varies by country -
            for example, US passports have 9 digits, UK passports have 9 digits, and German passports have a letter
            followed by 8-10 digits.
          </p>

          <h4 className='ds-u-margin-top--3'>Passport Issue Date</h4>
          <p>Enter the date your passport was issued, as shown on the information page of your passport.</p>

          <h4 className='ds-u-margin-top--3'>Passport Expiration Date</h4>
          <p>
            Enter the date your passport expires, as shown on the information page of your passport. Many countries
            require that your passport be valid for at least 6 months beyond your intended travel dates.
          </p>

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> Make sure all information matches exactly what appears on your passport document.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default PassportInformationPage;
